import { useRef } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, ChevronLeft, ChevronRight, Clock, Leaf } from "lucide-react";
import { useTranslation } from "react-i18next";
import { getOptimizedImageUrl, ImageSizes } from "@/lib/imageUtils";
import { trackClick } from "@/lib/analytics";

const parseLocalDate = (s) => {
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d);
};

const nextDateOf = (event, today) => {
  const dates = [
    ...(event.event_dates_times || []).map((d) => d.date),
    ...(event.event_dates || []),
    ...(event.fixed_date ? [event.fixed_date] : []),
  ]
    .filter(Boolean)
    .map(parseLocalDate)
    .filter((d) => d >= today)
    .sort((a, b) => a - b);
  return dates[0] || null;
};

const priceLabel = (event, t) => {
  if (event.is_external) return null;
  const prices = (event.ticket_types || []).map((x) => x.price || 0).filter((p) => p > 0);
  if (prices.length) return `${t("common.from")} €${Math.min(...prices)}`;
  if (event.price > 0) return `€${event.price}`;
  if (event.whatsapp_only_booking) return null;
  return t("common.free");
};

export const getUpcomingEvents = (events, limit = 6) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return events
    .map((e) => ({ event: e, next: nextDateOf(e, today) }))
    .filter(({ event, next }) => next || event.event_type === "appointment")
    .sort((a, b) => (a.next ? a.next.getTime() : Infinity) - (b.next ? b.next.getTime() : Infinity))
    .slice(0, limit);
};

const DateBadge = ({ date, locale }) => (
  <div className="absolute top-4 left-4 bg-white/95 backdrop-blur rounded-xl px-3 py-2 text-center shadow-md min-w-[56px]">
    <div className="text-2xl font-bold leading-none text-[#2C2C2C]" style={{ fontFamily: "'Playfair Display', serif" }}>
      {date.getDate()}
    </div>
    <div className="text-[11px] uppercase tracking-wider text-[#7e823a] font-semibold mt-1">
      {date.toLocaleDateString(locale, { month: "short" })}
    </div>
  </div>
);

const EventImage = ({ event, className }) => {
  const src = getOptimizedImageUrl(event.image_url, ImageSizes.CARD);
  if (!src) {
    return (
      <div className={`${className} bg-gradient-to-br from-[#dbe3c8] to-[#F5F5DC] flex items-center justify-center`}>
        <Leaf className="w-14 h-14 text-[#7e823a]/50" />
      </div>
    );
  }
  return (
    <img
      src={src}
      alt={event.title}
      loading="lazy"
      decoding="async"
      className={`${className} object-cover transition-transform duration-700 group-hover:scale-105`}
      style={{ objectPosition: `${event.image_position_x ?? 50}% ${event.image_position_y ?? 50}%` }}
    />
  );
};

const CarouselCard = ({ item, locale, t }) => {
  const { event, next } = item;
  const price = priceLabel(event, t);
  return (
    <Link
      to={`/esperienze/${event.id}`}
      onClick={() => trackClick("home-upcoming-card", { event_id: event.id })}
      className="group snap-start flex-shrink-0 w-[82%] sm:w-[46%] lg:w-[31.5%] relative rounded-3xl overflow-hidden h-[420px] shadow-md hover:shadow-xl transition-shadow duration-300"
      data-testid={`upcoming-card-${event.id}`}
    >
      <EventImage event={event} className="absolute inset-0 w-full h-full" />
      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
      {next && <DateBadge date={next} locale={locale} />}
      <div className="absolute bottom-0 inset-x-0 p-6 text-white">
        <h3 className="text-2xl font-bold leading-tight mb-2 line-clamp-2" style={{ fontFamily: "'Playfair Display', serif" }}>
          {event.title}
        </h3>
        <div className="flex items-center gap-3 text-sm text-white/85 mb-4">
          {(event.start_time || event.fixed_time) && (
            <span className="flex items-center gap-1"><Clock className="w-4 h-4" />{event.start_time || event.fixed_time}</span>
          )}
          {!next && <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4" />{t("events.appointment")}</span>}
        </div>
        <div className="flex items-center justify-between">
          {price && <span className="text-lg font-semibold">{price}</span>}
          <span className="inline-flex items-center gap-1 bg-white text-[#2C2C2C] rounded-full px-4 py-2 text-sm font-medium transition-transform duration-300 group-hover:translate-x-1">
            {t("common.book")} <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

const FeatureCard = ({ item, locale, t }) => {
  const { event, next } = item;
  const price = priceLabel(event, t);
  return (
    <Link
      to={`/esperienze/${event.id}`}
      onClick={() => trackClick("home-upcoming-card", { event_id: event.id })}
      className="group grid md:grid-cols-2 bg-white rounded-3xl overflow-hidden border border-[#E5E0D8] shadow-sm hover:shadow-xl transition-shadow duration-300"
      data-testid={`upcoming-feature-${event.id}`}
    >
      <div className="relative h-64 md:h-full md:min-h-[340px] overflow-hidden">
        <EventImage event={event} className="absolute inset-0 w-full h-full" />
        {next && <DateBadge date={next} locale={locale} />}
      </div>
      <div className="p-6 md:p-10 flex flex-col justify-center">
        {next && (
          <p className="text-sm uppercase tracking-wider text-[#7e823a] font-semibold mb-2">
            {next.toLocaleDateString(locale, { weekday: "long", day: "numeric", month: "long" })}
          </p>
        )}
        <h3 className="text-3xl font-bold text-[#2C2C2C] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
          {event.title}
        </h3>
        <p className="text-[#5C5C5C] leading-relaxed line-clamp-5 mb-6 whitespace-pre-line">{event.description}</p>
        <div className="flex items-center justify-between gap-4 mt-auto">
          {price && <span className="text-2xl font-bold text-[#7e823a]">{price}</span>}
          <span className="inline-flex items-center gap-2 bg-[#7e823a] text-white rounded-full px-6 py-3 font-medium transition-colors group-hover:bg-[#5e6228]">
            {t("common.bookNow")} <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </div>
    </Link>
  );
};

export const UpcomingEvents = ({ events, loading }) => {
  const { t, i18n } = useTranslation();
  const trackRef = useRef(null);
  const locale = i18n.language === "de" ? "de-DE" : i18n.language === "en" ? "en-US" : "it-IT";
  const items = getUpcomingEvents(events);
  const scroll = (dir) => trackRef.current?.scrollBy({ left: dir * trackRef.current.clientWidth * 0.8, behavior: "smooth" });

  if (!loading && items.length === 0) return null;

  return (
    <section data-reveal className="py-16 md:py-24 bg-background" data-testid="home-upcoming-events">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
          <div>
            <h2 className="text-4xl md:text-5xl font-bold text-[#2C2C2C] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>
              {t("events.title")}
            </h2>
            <p className="text-lg text-[#5C5C5C] max-w-2xl">{t("events.subtitle")}</p>
          </div>
          <div className="flex items-center gap-2">
            {items.length > 3 && (
              <div className="hidden md:flex gap-2">
                <button onClick={() => scroll(-1)} className="w-11 h-11 rounded-full border border-[#E5E0D8] bg-white hover:bg-[#dbe3c8]/40 flex items-center justify-center" aria-label="Precedenti" data-testid="upcoming-prev">
                  <ChevronLeft className="w-5 h-5 text-[#7e823a]" />
                </button>
                <button onClick={() => scroll(1)} className="w-11 h-11 rounded-full border border-[#E5E0D8] bg-white hover:bg-[#dbe3c8]/40 flex items-center justify-center" aria-label="Successivi" data-testid="upcoming-next">
                  <ChevronRight className="w-5 h-5 text-[#7e823a]" />
                </button>
              </div>
            )}
            <Link to="/esperienze" className="inline-flex items-center gap-1 text-[#7e823a] font-medium hover:underline ml-2 py-2" data-testid="upcoming-see-all">
              {t("events.seeAllEvents")} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        {loading ? (
          <div className="flex gap-6 overflow-hidden">
            {[0, 1, 2].map((i) => (
              <div key={i} className="skeleton-shimmer flex-shrink-0 w-[82%] sm:w-[46%] lg:w-[31.5%] h-[420px] rounded-3xl" />
            ))}
          </div>
        ) : items.length >= 3 ? (
          <div ref={trackRef} className="flex gap-6 overflow-x-auto snap-x snap-mandatory scroll-px-4 md:scroll-px-0 pb-4 -mx-4 px-4 md:mx-0 md:px-0 scrollbar-hide" data-testid="upcoming-carousel">
            {items.map((item) => (
              <CarouselCard key={item.event.id} item={item} locale={locale} t={t} />
            ))}
          </div>
        ) : (
          <div className="space-y-8" data-testid="upcoming-featured">
            {items.map((item) => (
              <FeatureCard key={item.event.id} item={item} locale={locale} t={t} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default UpcomingEvents;
